import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, DollarSign, Users, Clock } from "lucide-react";

const AnimatedCounter = ({ end, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const increment = end / 60;
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, end]);

  return (
    <span ref={ref}>
      {prefix}
      {count}
      {suffix}
    </span>
  );
};

const metrics = [
  { icon: TrendingUp, value: 40, suffix: "%", label: "Improvement in Collection Rates" },
  { icon: DollarSign, value: 60, suffix: "%", label: "Cost Reduction in Operations" },
  { icon: Users, value: 3, suffix: "x", label: "Agent Productivity Increase" },
  { icon: Clock, value: 4, suffix: "s", prefix: "<", label: "Voice Response Latency" },
];

export const CaseStudySection = () => (
  <section id="case-study" data-testid="case-study-section" className="py-24 md:py-32 relative">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-[#c9a84c] font-heading font-semibold text-sm tracking-widest uppercase mb-4">
            Case Study
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
            NBFC Voice <span className="text-gradient-gold">Collections</span>
          </h2>
          <p className="font-body text-muted-foreground mt-6 leading-relaxed">
            A leading NBFC partnered with Ojas Vani to automate their collections
            process. Our voice AI platform handled repayment reminders, EMI
            negotiations, and PTP confirmations — in Hindi, English, and Hinglish
            — with full regulatory compliance.
          </p>
          <div className="mt-8 space-y-4">
            {[
              "Multilingual voice interactions (Hindi, English, Hinglish)",
              "Real-time telephony integration with Exotel",
              "FSM + agentic workflows for loan servicing",
              "End-to-end encryption with audit logs",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] mt-2 shrink-0" />
                <span className="font-body text-muted-foreground text-sm">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-4">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="glass-card rounded-lg p-6 text-center"
              data-testid={`metric-${i}`}
            >
              <m.icon className="h-6 w-6 text-[#c9a84c] mx-auto mb-3" />
              <p className="font-heading font-bold text-3xl text-foreground">
                <AnimatedCounter end={m.value} suffix={m.suffix} prefix={m.prefix || ""} />
              </p>
              <p className="font-body text-muted-foreground text-xs mt-2">{m.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
